import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * Hook to sync WebSocket events with React Query cache
 *
 * This hook listens to WebSocket messages for data change events
 * and automatically invalidates the relevant React Query caches.
 *
 * @example
 * ```ts
 * // In your App.tsx or a top-level component
 * function App() {
 *   useWebSocketQuerySync(); // Enable WebSocket → React Query sync
 *   return <Router>...</Router>;
 * }
 * ```
 *
 * Supported WebSocket events:
 * - asset:created, asset:updated, asset:deleted
 * - form:created, form:updated, form:deleted, form:published
 * - submission:created, submission:updated, submission:approved, submission:rejected
 * - user:created, user:updated, user:deleted
 * - dashboard:refresh
 */
export function useWebSocketQuerySync() {
  const queryClient = useQueryClient();
  const { socket, isConnected } = useWebSocket();

  useEffect(() => {
    if (!socket || !isConnected) return;

    // The DryConnect client delivers ALREADY-PARSED messages to onMessage
    // listeners (not a raw MessageEvent), so consume the object directly.
    const handleMessage = (message: any) => {
      try {
        const { event: eventType, data } = message ?? {};

        console.log('🔄 QuerySync: Received event:', eventType);

        // Route to appropriate cache invalidation based on event type
        switch (eventType) {
          // Asset events
          case 'asset:created':
          case 'asset:updated':
          case 'asset:deleted':
          case 'asset:archived':
          case 'asset:unarchived':
            handleAssetEvent(eventType, data);
            break;

          // Form events
          case 'form:created':
          case 'form:updated':
          case 'form:deleted':
          case 'form:published':
          case 'form:archived':
          case 'form:unarchived':
            handleFormEvent(eventType, data);
            break;

          // Submission events
          case 'submission:created':
          case 'submission:updated':
          case 'submission:approved':
          case 'submission:rejected':
            handleSubmissionEvent(eventType, data);
            break;

          // User events
          case 'user:created':
          case 'user:updated':
          case 'user:deleted':
            handleUserEvent(eventType, data);
            break;

          // Dashboard refresh
          case 'dashboard:refresh':
            handleDashboardRefresh();
            break;

          // Generic data refresh
          case 'data:refresh':
            handleDataRefresh(data);
            break;

          default:
            // Unknown event - do nothing
            break;
        }
      } catch (error) {
        // Ignore parse errors - message might not be JSON
      }
    };

    const handleAssetEvent = (eventType: string, data?: { id?: string }) => {
      console.log('🔄 QuerySync: Invalidating asset caches');

      // Always invalidate asset lists
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.lists() });

      // Invalidate specific asset if ID provided
      if (data?.id) {
        if (eventType === 'asset:deleted') {
          queryClient.removeQueries({ queryKey: queryKeys.assets.detail(data.id) });
        } else {
          queryClient.invalidateQueries({ queryKey: queryKeys.assets.detail(data.id) });
        }
      }

      // Also refresh dashboard stats
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats() });
    };

    const handleFormEvent = (eventType: string, data?: { id?: string }) => {
      console.log('🔄 QuerySync: Invalidating form caches');

      // Always invalidate form lists
      queryClient.invalidateQueries({ queryKey: queryKeys.forms.lists() });

      // Invalidate specific form if ID provided
      if (data?.id) {
        if (eventType === 'form:deleted') {
          queryClient.removeQueries({ queryKey: queryKeys.forms.detail(data.id) });
        } else {
          queryClient.invalidateQueries({ queryKey: queryKeys.forms.detail(data.id) });
        }
      }

      // Also refresh dashboard stats
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats() });
    };

    const handleSubmissionEvent = (eventType: string, data?: { id?: string }) => {
      console.log('🔄 QuerySync: Invalidating submission caches');

      // Always invalidate submission lists
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.lists() });

      // Invalidate specific submission if ID provided
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.submissions.detail(data.id) });
      }

      // Refresh dashboard stats (pending approvals, etc.)
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats() });

      // Refresh analytics
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all() });
    };

    const handleUserEvent = (eventType: string, data?: { id?: string }) => {
      console.log('🔄 QuerySync: Invalidating user caches');

      // Always invalidate user lists
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });

      // Invalidate specific user if ID provided
      if (data?.id) {
        if (eventType === 'user:deleted') {
          queryClient.removeQueries({ queryKey: queryKeys.users.detail(data.id) });
        } else {
          queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(data.id) });
        }
      }
    };

    const handleDashboardRefresh = () => {
      console.log('🔄 QuerySync: Refreshing dashboard');
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.stats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all() });
    };

    const handleDataRefresh = (data?: { entities?: string[] }) => {
      console.log('🔄 QuerySync: Generic data refresh', data);

      const entities = data?.entities || ['all'];

      if (entities.includes('all') || entities.includes('assets')) {
        queryClient.invalidateQueries({ queryKey: queryKeys.assets.all() });
      }
      if (entities.includes('all') || entities.includes('forms')) {
        queryClient.invalidateQueries({ queryKey: queryKeys.forms.all() });
      }
      if (entities.includes('all') || entities.includes('submissions')) {
        queryClient.invalidateQueries({ queryKey: queryKeys.submissions.all() });
      }
      if (entities.includes('all') || entities.includes('users')) {
        queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      }
      if (entities.includes('all') || entities.includes('dashboard')) {
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all() });
      }
      if (entities.includes('all') || entities.includes('analytics')) {
        queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all() });
      }
    };

    // Subscribe via the DryConnect client API (no raw addEventListener exists on
    // the wrapper). onMessage returns an unsubscribe function.
    const unsubscribe = socket.onMessage?.(handleMessage);

    // Cleanup
    return () => {
      unsubscribe?.();
    };
  }, [socket, isConnected, queryClient]);
}

export default useWebSocketQuerySync;
